<template>
  <div>
    <v-file-input
      v-if="!readonly && !currentFilePath"
      v-model="selectedFile"
      :label="$t('label.file_upload')"
      outlined
      dense
      :accept="acceptExtensions.join(',')"
      prepend-icon="mdi-file-upload"
      @change="handleFileChange"
      :error-messages="errorMessage"
    ></v-file-input>

    <div v-if="currentFilePath" class="d-flex align-center">
      <v-icon class="mr-2">mdi-file-document-outline</v-icon>
      <span class="text-truncate">{{ getFileName(currentFilePath) }}</span>
      <v-btn v-if="!readonly" icon small color="red" @click="handleDelete">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
      <v-btn icon small color="primary" @click="handleDownload">
        <v-icon small>mdi-download</v-icon>
      </v-btn>
    </div>

    <div v-if="readonly && !this.currentFilePath">
      <v-icon>mdi-report</v-icon>
      <span class="text-truncate">{{ $t("txt.no_file") }}</span>
    </div>

    <v-progress-linear
      v-if="uploading"
      :value="uploadProgress"
      color="primary"
      height="25"
    >
      <template v-slot:default>
        <strong>{{ uploadProgress }}%</strong>
      </template>
    </v-progress-linear>
  </div>
</template>

<script>
import { fileApi } from "@/api/api";

export default {
  name: "FileInput",
  props: {
    initialFilePath: {
      type: String,
      default: "",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    acceptExtensions: {
      type: Array,
      default: () => [".pdf"],
    },
    category: {
      type: String,
      default: "general",
    },
  },
  data() {
    return {
      selectedFile: null,
      currentFilePath: "",
      uploading: false,
      uploadProgress: 0,
      errorMessage: "",
    };
  },
  watch: {
    initialFilePath: {
      immediate: true,
      handler(newPath) {
        this.currentFilePath = newPath;
      },
    },
  },
  methods: {
    getFileName(path) {
      return path ? path.split("/").pop() : "";
    },
    getFileExtension(filename) {
      return filename
        .slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2)
        .toLowerCase();
    },
    async handleFileChange(file) {
      if (!file) {
        this.errorMessage = "";
        return;
      }

      // 파일 크기 체크 (10MB 제한)
      if (file.size > 10 * 1024 * 1024) {
        this.errorMessage = this.$t("valid.file_size_limit");
        this.selectedFile = null;
        return;
      }

      // 파일 확장자 체크
      const fileExtension = this.getFileExtension(file.name);
      const allowedExtensions = this.acceptExtensions.map((ext) =>
        ext.startsWith(".") ? ext.slice(1) : ext
      );

      if (!allowedExtensions.includes(fileExtension)) {
        this.errorMessage = this.$t("valid.file_type_extension", {
          extension: this.acceptExtensions.join(","),
        });
        this.selectedFile = null;
        return;
      }

      this.errorMessage = "";
      this.uploading = true;

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("category", this.category);

        const response = await fileApi.uploadFile(formData, {
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          },
        });

        if (response.status === 200) {
          this.currentFilePath = response.data.path;
          this.$emit("update-saved-file-path", response.data.path);
        }
      } catch (error) {
        this.errorMessage = this.$t("valid.file_upload_failed");
        // eslint-disable-next-line no-console
        console.error("File upload failed:", error);
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
        this.selectedFile = null;
      }
    },
    async handleDelete() {
      try {
        if (this.currentFilePath) {
          await fileApi.deleteFile({ file_name: this.currentFilePath });
          this.currentFilePath = "";
          this.$emit("update-saved-file-path", "");
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("File deletion failed:", error);
      }
    },
    async handleDownload() {
      try {
        if (this.currentFilePath) {
          const fileName = this.getFileName(this.currentFilePath);
          const response = await fileApi.fileDownload({
            file_name: this.currentFilePath,
          });

          if (response && response.status === 200) {
            const mimeType = response.headers["content-type"];
            const blob = new Blob([response.data], { type: mimeType });
            const fileURL = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = fileURL;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(fileURL);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("File download failed:", error);
      }
    },
  },
};
</script>

<style scoped>
.v-file-input {
  max-width: 100%;
}
</style>
